import { useCallback } from "react"
import { batch, useSignal } from "@preact/signals-react"
import { postSubmit } from "@ui-seeker/utils"

import { useToast } from "@hooks"
import { customerSelectedID } from "@signals"
import { MODE_BY_SETUP, PRICING_CONFIG_APIS } from "../@constants"
import usePricingConfigDraft from "./usePricingConfigDraft"
import {
    customerSetup,
    havePendingChanges,
    mainSitesData,
    modeChangeAcknowledged,
    pricingConfigMode,
    reloadCustomerSetup,
    reloadPricingConfigData,
    rrspData
} from "../@signals"


/** Send the staged changes.
 *
 * Two things this hook is careful about:
 *
 * - **only the touched rows travel.** Sending the whole table would make the backend guess what
 *   changed, and an untouched RRSP row belongs to other customers too (the table is global per
 *   SKU × market), so re-sending it is a chance to move a price nobody asked to move;
 * - **the role change rides on the acknowledgment**, not on the mode selector. `applyRoleChange` is
 *   true only when the chosen mode differs from what the customer *is* **and** the checkbox in
 *   `ModeChangeWarning` is ticked. The endpoint refuses to touch `DIPI_USER.role` without that flag,
 *   so the warning is a gate and not decoration.
 */
export default function usePricingConfigSave() {

    const { showToast } = useToast()
    const { discardChanges } = usePricingConfigDraft()
    const isSaving = useSignal(false)

    const currentMode = MODE_BY_SETUP[customerSetup.value?.mode]
    const isModeChanging = !!currentMode && currentMode !== pricingConfigMode.value
    const applyRoleChange = isModeChanging && modeChangeAcknowledged.value

    const touchedMainSites = mainSitesData.value.filter(row => !!row.pending)
    const touchedRrspRows = rrspData.value.filter(row => !!row.pending)
    const canSave = !!customerSelectedID.value
        && (applyRoleChange || !!touchedMainSites.length || !!touchedRrspRows.length)

    const buildPayload = () => ({
        customerID: customerSelectedID.value,
        mode: pricingConfigMode.value,
        applyRoleChange,
        mainSites: touchedMainSites.map(({ country, siteID, pending }) => ({
            country, siteID, pending
        })),
        rrspRows: touchedRrspRows.map(
            ({ rrspID, market, dateFrom, dateTo, skuID, price, currency, pending }) => ({
                rrspID, market, dateFrom, dateTo, skuID, price, currency, pending
            })
        )
    })

    const handleSave = useCallback(() => {
        if (!canSave) return

        isSaving.value = true
        showToast.promise(
            postSubmit(PRICING_CONFIG_APIS.SAVE, buildPayload())
                .then(res => {
                    batch(() => {
                        havePendingChanges.value = false
                        modeChangeAcknowledged.value = false
                        isSaving.value = false
                        // Re-read instead of patching the tables in place: the server assigns
                        // `id_rrsp` and may have resolved the roles differently than we assumed.
                        reloadPricingConfigData.value = true
                        // A role change moves what `/setup` answers, so the summary and the
                        // preselected mode have to come again from the server.
                        if (applyRoleChange) reloadCustomerSetup.value = true
                    })
                    return res
                })
                .catch(error => {
                    isSaving.value = false
                    throw error
                }),
            {
                loading: 'Saving...',
                success: 'Configuration saved',
            }
        )
    }, [canSave, applyRoleChange, touchedMainSites.length, touchedRrspRows.length])

    return {
        canSave,
        isSaving: isSaving.value,
        applyRoleChange,
        pendingCount: touchedMainSites.length + touchedRrspRows.length,
        handleSave,
        discardChanges
    }
}
